import { GWCA } from '~/lib/ca-constants';

/**
 * Utility class for handling requests, notifications, and other utility functions related to customer accounts.
 */
export class GWCustomerAccountsUtils {
  constructor() {}

  /**
   * Returns the component data from the component's shadow DOM.
   * @returns {Object} - The component data.
   */
  get componentData() {
    const gwCustomerAccounts = document.querySelector('gw-customer-accounts');
    const componentDataTag = gwCustomerAccounts.shadowRoot.querySelector('[data-component-data]');
    return JSON.parse(componentDataTag.textContent);
  }

  /**
   * Returns the customer's ID from the component data.
   * @returns {string} - The customer's ID.
   */
  get customerId() {
    return this.componentData?.customer?.id;
  }

  /**
   * Returns the customer's first name from the component data.
   * @returns {string} - The customer's first name.
   */
  get customerFirstName() {
    return this.componentData?.customer?.firstName;
  }

  /**
   * Returns the customer's email address from local storage.
   * @returns {string} - The customer's email address.
   */
  get customerEmail() {
    return localStorage.getItem(GWCA.STORAGE_KEYS.EMAIL);
  }

  /**
   * Returns true if the customer has a customer ID.
   * @returns {string} - The customer's ID.
   */
  get hasCustomer() {
    return !!this.componentData?.customer?.id;
  }

  /**
   * Returns the customer's token from local storage.
   * @returns {string} - The customer's token.
   */
  get customerToken() {
    return localStorage.getItem(GWCA.STORAGE_KEYS.TOKEN);
  }

  /**
   * Returns the base URL from the component data.
   * @returns {string} - The base URL.
   */
  get baseURL() {
    return this.componentData.baseUrl;
  }

  /**
   * Returns the app element.
   * @returns {Element} - The app element.
   */
  get app() {
    return document.querySelector('gw-customer-accounts');
  }

  /**
   * Returns the shadow DOM of the app.
   * @returns {ShadowRoot} - The shadow DOM of the app.
   */
  get appShadow() {
    return this.app.shadowRoot;
  }

  /**
   * Returns the dashboard web component.
   * @returns {Element} - The dashboard web component.
   */
  get dashboard() {
    return this.appShadow.querySelector('gw-customer-accounts-dashboard');
  }

  /**
   * Returns the tab ID based on the current URL hash.
   * @returns {string} - The tab ID.
   */
  get tabId() {
    return GWCA.MAP[window.location.hash] || 'dashboard';
  }

  /**
   * Makes an HTTP request to the specified URL with the given body and method.
   *
   * @param {string} url - The endpoint URL to send the request to.
   * @param {Object} body - The request payload.
   * @param {string} [method='POST'] - The HTTP method to use (GET or POST).
   * @returns {Promise<Object>} - The response data as a JSON object.
   */
  async request(url, body = {}, method = 'POST') {
    const requestConfig = () => {
      const config = {
        method: method,
        mode: 'cors',
        cache: 'no-cache',
        headers: {
          'Content-Type': 'application/json',
        },
      };

      if (method === 'POST') {
        config.body = JSON.stringify(body);
      }

      return config;
    };

    if (method === 'GET') {
      const params = new URLSearchParams(body);
      url = `${url}?${params}`;
    }

    if (method === 'POST') {
      body.token = this.customerToken;
      body.email = this.customerEmail;
      body.customerID = this.customerId;
    }

    const response = await fetch(this.baseURL + url, requestConfig());

    return await response.json();
  }

  /**
   * Displays a notification message.
   * @param {string} message - The message to display.
   * @param {string} [variant='primary'] - The variant of the notification (e.g., 'primary', 'success', 'danger').
   * @param {number} [duration=3000] - The duration the notification should be displayed (in milliseconds).
   * @returns {Promise<void>} - A promise that resolves when the notification is displayed.
   */
  notify(message, variant = 'success', duration = 3000) {
    const gwCustomerAccounts = document.querySelector('gw-customer-accounts');
    const notificationAlert = gwCustomerAccounts.shadowRoot.querySelector('[data-notification-alert]');
    const notificationMessage = gwCustomerAccounts.shadowRoot.querySelector('[data-notification-message]');

    notificationMessage.innerHTML = message;
    notificationAlert.open = true;
  }

  /**
   * Updates the content of the app with the specified elements.
   * Fetches the content using Section Rendering API.
   * @param {Array} elementsToUpdate - The elements to update.
   * @returns {Promise<void>} - A promise that resolves when the content is updated.
   * @async
   */
  async updateAjaxContent(elementsToUpdate) {
    return await this.app.updateContent(elementsToUpdate);
  }

  /**
   * Parses a Shopify variant/product ID to extract the numeric ID at the end.
   * @param {string} id - The Shopify product/variant ID.
   * @returns {string} The parsed ID.
   */
  parseVariantId(id) {
    return String(id).split('/').pop();
  }

  /**
   * Converts cents to dollars and formats the amount.
   * @param {number} amount - The amount in cents.
   */
  formatMoney(amount) {
    return (amount / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });
  }

  /**
   * Adds values to the storage with the specified key.
   * @param {string} key - The key to update.
   * @param {Array} values - The values to add.
   * @param {string} [storageType='local'] - The type of storage to update (local or session).
   * @param {position} [position='start'] - The position to add the values (start or end).
   * @returns {void}
   */
  addToStorage({ key, values, storageType = 'local', position = 'start' }) {
    const storage = storageType === 'local' ? localStorage : sessionStorage;
    const currentValue = JSON.parse(storage.getItem(key)) || [];

    if (position === 'start') {
      storage.setItem(key, JSON.stringify([...values, ...currentValue]));
    } else {
      storage.setItem(key, JSON.stringify([...currentValue, ...values]));
    }
  }

  /**
   * Removes a value from the storage with the specified key.
   * @param {string} key - The key to update.
   * @param {Array} values - The values to remove.
   * @param {string} [storageType='local'] - The type of storage to update (local or session).
   * @param {string} [nestedKey] - The nested key to remove.
   * @returns {void}
   */
  removeFromStorage({ key, values, storageType = 'local', nestedKey }) {
    const storage = storageType === 'local' ? localStorage : sessionStorage;
    const currentValue = JSON.parse(storage.getItem(key)) || [];

    if (nestedKey) {
      const updatedValue = currentValue.map((item) => {
        if (values.includes(item[nestedKey])) {
          return null;
        }

        return item;
      });
      storage.setItem(key, JSON.stringify(updatedValue.filter(Boolean)));
    } else {
      const updatedValue = currentValue.filter((item) => !values.includes(item));
      storage.setItem(key, JSON.stringify(updatedValue));
    }
  }

  /**
   * Returns the status of an egift.
   * @param {Object} egiftData - The egift data.
   * @returns {string} The status of the egift.
   */
  getEgiftStatus(egiftData) {
    if (egiftData.egift_redeemed) {
      return 'redeemed';
    } else if (egiftData.email_open_sent && egiftData.email_sent) {
      return 'opened';
    } else if (egiftData.email_sent) {
      return 'sent';
    } else {
      return 'scheduled';
    }
  }

  /**
   * Converts a date string to a Date object.
   * @param {string} dateString - The date string to convert.
   * @returns {Date} The Date object.
   */
  toLocalDate(dateString) {
    const [month, day, year] = dateString.split('/').map(Number);
    return new Date(year, month - 1, day);
  }
}

export const GWUtils = new GWCustomerAccountsUtils();
